// src/pages/ProductDetail.jsx (o src/components/ProductDetail.jsx)
import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { db } from '../firebase';
import { doc, getDoc } from 'firebase/firestore';
import ProductosRelacionados from '../components/ProductosRelacionados';
import { useAfiliado } from '../context/AfiliadoContext'; // <-- Importamos el contexto

const WHATSAPP_NUMERO = '593993334684';

const getOptimizedUrl = (url, width = 1200) => {
  if (!url || !url.includes('cloudinary.com') || !url.includes('/upload/')) return url;
  const [base, resto] = url.split('/upload/');
  const segmentos = resto.split('/');
  const esTransformacion = /(^|,)(w_|h_|c_|q_|f_|e_|g_|b_|ar_|dpr_|fl_)/.test(segmentos[0]);
  if (esTransformacion) segmentos.shift();
  return `${base}/upload/w_${width},c_limit,q_auto,f_auto/${segmentos.join('/')}`;
};

const formatearDescripcion = (html = '') => {
  if (!html) return '';
  const partes = html.split(/<br\s*\/?>/gi);
  const formateadas = partes
    .map((parte) => {
      let texto = parte.trim();
      if (!texto) return '';
      const match = texto.match(/^(.*?)((?:<\/[^>]+>)*)$/s);
      const contenido = match ? match[1] : texto;
      const tagsFinales = match ? match[2] : '';
      const ultimoChar = contenido.trim().slice(-1);
      const yaTienePuntuacion = ['.', '!', '?', ':', ';'].includes(ultimoChar);
      const contenidoFinal = yaTienePuntuacion || !ultimoChar ? contenido : `${contenido}.`;
      return `${contenidoFinal}${tagsFinales}`;
    })
    .filter(Boolean);
  return formateadas.join('<br/>');
};

// ─── Secciones que NUNCA deben ser tabs principales (sub-categorías dentro de Composición, etc.)
const RE_SUBSECCION = [
  /^vitamina/i, /^mineral/i, /^nutriente/i, /^prote[ií]na/i,
  /^carbohidrato/i, /^l[ií]pido/i, /^grasa/i, /^[aá]cido/i,
  /^amino/i, /^antioxidante/i, /^fibra/i, /^az[uú]car/i,
];

// ─── Secciones conocidas como principales (chips, tabs, alertas) ──────────────
const RE_SECCION_PRINCIPAL = [
  /descripci[oó]n/i, /beneficio/i, /propiedad/i, /ventaja/i, /utilidad/i,
  /composici[oó]n/i, /ingrediente/i, /f[oó]rmula/i, /componente/i, /activo/i,
  /modo de uso/i, /instrucci[oó]n/i, /c[oó]mo usar/i, /dosificaci[oó]n/i,
  /dosis/i, /aplicaci[oó]n/i, /usos?$/i, /indicaci[oó]n/i,
  /caracter[ií]stica/i, /especificaci[oó]n/i, /resultado/i,
  /presentaci[oó]n/i, /sabor/i, /aroma/i, /fragancia/i,
  /tama[nñ]o/i, /peso/i, /formato/i, /volumen/i,
  /advertencia/i, /precauci[oó]n/i, /aviso/i, /nota importante/i,
  /contraindicaci[oó]n/i, /alergia/i, /informaci[oó]n/i,
];

// ─── Clasificación por tipo ───────────────────────────────────────────────────
const KEYS_DATO = [
  'presentación', 'presentacion', 'sabor', 'tamaño', 'tamano', 'peso',
  'cantidad', 'contenido', 'formato', 'fragancia', 'aroma', 'color',
  'volumen', 'unidad', 'unidades',
];
const KEYS_ALERTA = [
  'advertencia', 'precaución', 'precaucion', 'aviso', 'nota importante',
  'contraindicación', 'contraindicacion', 'alergia', 'alergias',
];

const clasificarTipo = (titulo) => {
  const t = titulo.toLowerCase().trim();
  if (KEYS_ALERTA.some((k) => t.includes(k))) return 'alerta';
  if (KEYS_DATO.some((k) => t.includes(k)))   return 'dato';
  return 'tab';
};

// ─── Parser principal ─────────────────────────────────────────────────────────
// Escanea el TEXTO COMPLETO (no línea por línea) buscando patrones "Título:"
// Funciona para descripciones guardadas como texto plano O con etiquetas <br>
const parseDescripcion = (html = '') => {
  if (!html) return null;

  // Normalizar: <br> → \n, eliminar otras etiquetas HTML
  const texto = html
    .replace(/<br\s*\/?>/gi, '\n')
    .replace(/<strong>(.*?)<\/strong>/gi, '$1')
    .replace(/<[^>]+>/g, '')
    .replace(/[ \t]+/g, ' ')   // colapsar espacios (no newlines)
    .trim();

  if (!texto) return null;

  // Regex global: busca "Título:" en cualquier posición del texto
  // Acepta 1-5 palabras con primera letra mayúscula
  const reTitulo = /([A-ZÁÉÍÓÚÑÜ][a-záéíóúñüA-ZÁÉÍÓÚÑÜ]+(?:[ \t]+[a-záéíóúñüA-ZÁÉÍÓÚÑÜ]+){0,4}):\s*/g;

  const todosLosMatches = [];
  let m;
  while ((m = reTitulo.exec(texto)) !== null) {
    todosLosMatches.push({
      titulo: m[1].trim(),
      matchStart: m.index,
      contentStart: m.index + m[0].length,
    });
  }

  if (todosLosMatches.length === 0) return null;

  // Filtrar: mantener solo secciones principales válidas
  const matchesFiltrados = todosLosMatches.filter(({ titulo, matchStart }) => {
    // 1) Nunca son tabs principales (vitaminas, minerales, etc.)
    if (RE_SUBSECCION.some((re) => re.test(titulo))) return false;
    // 2) Si es una sección conocida → aceptar siempre
    if (RE_SECCION_PRINCIPAL.some((re) => re.test(titulo))) return true;
    // 3) Para secciones desconocidas: rechazar si están precedidas por ':'
    //    (indica que es un sub-encabezado dentro de otra sección)
    let i = matchStart - 1;
    while (i >= 0 && texto[i] === ' ') i--;
    return i < 0 || texto[i] !== ':';
  });

  if (matchesFiltrados.length < 2) return null;

  // Construir secciones con su contenido
  const secciones = [];
  for (let i = 0; i < matchesFiltrados.length; i++) {
    const { titulo, contentStart } = matchesFiltrados[i];
    const contentEnd = i + 1 < matchesFiltrados.length
      ? matchesFiltrados[i + 1].matchStart
      : texto.length;

    const rawContenido = texto.slice(contentStart, contentEnd).replace(/\s+$/, '').trim();
    if (!rawContenido) continue;

    // Intentar dividir en ítems:
    // 1) Por saltos de línea (cuando el HTML original tenía <br>)
    let items = rawContenido.split('\n').map((s) => s.trim()).filter((s) => s.length > 2);

    // 2) Si quedó 1 solo bloque, intentar dividir por ". Capital" (frases separadas por punto)
    if (items.length <= 1) {
      const porPunto = rawContenido
        .split(/\.\s+(?=[A-ZÁÉÍÓÚÑÜ])/)
        .map((s) => s.trim())
        .filter((s) => s.length > 5);
      if (porPunto.length > 1) items = porPunto;
    }

    secciones.push({
      titulo,
      tipo: clasificarTipo(titulo),
      items: items.length > 0 ? items : [rawContenido],
    });
  }

  return secciones.length >= 2 ? secciones : null;
};

// ─── Íconos por tipo de sección ───────────────────────────────────────────────
const ICONOS_SECCION = {
  descripción: (
    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <line x1="17" y1="10" x2="3" y2="10"/><line x1="21" y1="6" x2="3" y2="6"/>
      <line x1="21" y1="14" x2="3" y2="14"/><line x1="17" y1="18" x2="3" y2="18"/>
    </svg>
  ),
  beneficio: (
    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/><polyline points="22 4 12 14.01 9 11.01"/>
    </svg>
  ),
  composición: (
    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M9 3H5a2 2 0 0 0-2 2v4m6-6h10a2 2 0 0 1 2 2v4M9 3v18m0 0h10a2 2 0 0 0 2-2V9M9 21H5a2 2 0 0 1-2-2V9m0 0h18"/>
    </svg>
  ),
  ingrediente: (
    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M9 3H5a2 2 0 0 0-2 2v4m6-6h10a2 2 0 0 1 2 2v4M9 3v18m0 0h10a2 2 0 0 0 2-2V9M9 21H5a2 2 0 0 1-2-2V9m0 0h18"/>
    </svg>
  ),
  'modo de uso': (
    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/>
    </svg>
  ),
  uso: (
    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/>
    </svg>
  ),
  instrucción: (
    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/>
    </svg>
  ),
};

const getIconoSeccion = (titulo) => {
  const t = titulo.toLowerCase();
  for (const [key, icon] of Object.entries(ICONOS_SECCION)) {
    if (t.includes(key)) return icon;
  }
  return null;
};

// ─── Componente TabsDescripcion ───────────────────────────────────────────────
const TabsDescripcion = ({ secciones }) => {
  const tabs    = secciones.filter((s) => s.tipo === 'tab');
  const datos   = secciones.filter((s) => s.tipo === 'dato');
  const alertas = secciones.filter((s) => s.tipo === 'alerta');

  const [tabActiva, setTabActiva] = React.useState(tabs[0]?.titulo ?? '');
  const seccionActiva = tabs.find((t) => t.titulo === tabActiva);

  return (
    <div className="dtabs-root">

      {/* ── Datos rápidos (chips informativos) ── */}
      {datos.length > 0 && (
        <div className="dtabs-chips">
          {datos.map((d) => (
            <div key={d.titulo} className="dtabs-chip">
              <span className="dtabs-chip__label">{d.titulo}</span>
              <span className="dtabs-chip__value">{d.items.join(' · ')}</span>
            </div>
          ))}
        </div>
      )}

      {/* ── Navegación de pestañas ── */}
      {tabs.length > 1 && (
        <div
          className="dtabs-nav"
          role="tablist"
          aria-label="Secciones del producto"
        >
          {tabs.map((t) => {
            const activa = tabActiva === t.titulo;
            const icono  = getIconoSeccion(t.titulo);
            return (
              <button
                key={t.titulo}
                role="tab"
                aria-selected={activa}
                aria-controls={`dtab-panel-${t.titulo}`}
                id={`dtab-${t.titulo}`}
                className={`dtabs-tab${activa ? ' is-active' : ''}`}
                onClick={() => setTabActiva(t.titulo)}
              >
                {icono && <span className="dtabs-tab__icon">{icono}</span>}
                {t.titulo}
              </button>
            );
          })}
        </div>
      )}

      {/* Título cuando solo hay una sección */}
      {tabs.length === 1 && (
        <p className="dtabs-single-title">{tabs[0].titulo}</p>
      )}

      {/* ── Contenido del panel activo ── */}
      {seccionActiva && (
        <div
          key={tabActiva}
          role="tabpanel"
          id={`dtab-panel-${tabActiva}`}
          aria-labelledby={`dtab-${tabActiva}`}
          className="dtabs-panel"
        >
          {seccionActiva.items.length === 1 ? (
            <p className="dtabs-text">{seccionActiva.items[0]}</p>
          ) : (
            <ul className="dtabs-list" aria-label={seccionActiva.titulo}>
              {seccionActiva.items.map((item, i) => (
                <li key={i} className="dtabs-list__item">
                  <span className="dtabs-list__dot" aria-hidden="true" />
                  <span>{item}</span>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}

      {/* ── Alertas / Advertencias ── */}
      {alertas.map((a) => (
        <div key={a.titulo} className="dtabs-alerta" role="alert">
          <span className="dtabs-alerta__icon" aria-hidden="true">
            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"/>
              <line x1="12" y1="9" x2="12" y2="13"/><line x1="12" y1="17" x2="12.01" y2="17"/>
            </svg>
          </span>
          <div>
            <strong className="dtabs-alerta__titulo">{a.titulo}</strong>
            <span className="dtabs-alerta__texto">{a.items.join(' ')}</span>
          </div>
        </div>
      ))}
    </div>
  );
};

// ─── CSS de las pestañas (se inyecta en el <style> del componente) ────────────
const DTABS_CSS = `
  .dtabs-root {
    display: flex;
    flex-direction: column;
    gap: var(--space-3);
  }

  /* Chips de datos rápidos */
  .dtabs-chips {
    display: flex;
    flex-wrap: wrap;
    gap: 6px;
  }
  .dtabs-chip {
    display: inline-flex;
    align-items: center;
    gap: 5px;
    background: var(--color-gris-fondo);
    border: 1px solid var(--border-default);
    border-radius: var(--radius-pill);
    padding: 4px 11px;
    font-size: 12px;
    line-height: 1;
  }
  .dtabs-chip__label {
    color: var(--text-muted);
    font-weight: var(--font-weight-medium);
  }
  .dtabs-chip__label::after { content: ':'; }
  .dtabs-chip__value {
    color: var(--text-primary);
    font-weight: var(--font-weight-medium);
  }

  /* Barra de tabs */
  .dtabs-nav {
    display: flex;
    gap: 0;
    overflow-x: auto;
    scrollbar-width: none;
    border-bottom: 2px solid var(--border-default);
    -webkit-overflow-scrolling: touch;
    margin-bottom: 0;
  }
  .dtabs-nav::-webkit-scrollbar { display: none; }

  .dtabs-tab {
    display: inline-flex;
    align-items: center;
    gap: 5px;
    padding: 8px 14px;
    background: none;
    border: none;
    border-bottom: 2.5px solid transparent;
    margin-bottom: -2px;
    font-family: var(--font-body);
    font-size: 12.5px;
    font-weight: var(--font-weight-medium);
    color: var(--text-muted);
    cursor: pointer;
    white-space: nowrap;
    border-radius: var(--radius-sm) var(--radius-sm) 0 0;
    transition: color var(--transition-fast), border-color var(--transition-fast), background-color var(--transition-fast);
    -webkit-tap-highlight-color: transparent;
    user-select: none;
  }
  .dtabs-tab:hover:not(.is-active) {
    color: var(--text-secondary);
    background: var(--color-gris-fondo);
  }
  .dtabs-tab.is-active {
    color: var(--color-verde);
    border-bottom-color: var(--color-verde);
    font-weight: 600;
  }
  .dtabs-tab__icon {
    display: flex;
    align-items: center;
    flex-shrink: 0;
    opacity: 0.8;
  }

  /* Título solo cuando hay 1 tab */
  .dtabs-single-title {
    font-size: var(--font-size-label);
    font-weight: var(--font-weight-medium);
    letter-spacing: var(--letter-spacing-wider);
    text-transform: uppercase;
    color: var(--text-muted);
    margin: 0;
  }

  /* Panel con animación de entrada */
  .dtabs-panel {
    animation: dtabsFadeIn 180ms ease both;
  }
  @keyframes dtabsFadeIn {
    from { opacity: 0; transform: translateY(5px); }
    to   { opacity: 1; transform: translateY(0); }
  }

  /* Texto párrafo */
  .dtabs-text {
    font-size: var(--font-size-small);
    font-weight: var(--font-weight-light);
    line-height: var(--line-height-loose);
    color: var(--text-secondary);
    margin: 0;
  }

  /* Lista de ítems */
  .dtabs-list {
    list-style: none;
    padding: 0;
    margin: 0;
    display: flex;
    flex-direction: column;
    gap: var(--space-2);
  }
  .dtabs-list__item {
    display: flex;
    align-items: flex-start;
    gap: 10px;
    font-size: var(--font-size-small);
    font-weight: var(--font-weight-light);
    line-height: var(--line-height-normal);
    color: var(--text-secondary);
  }
  .dtabs-list__dot {
    flex-shrink: 0;
    width: 5px;
    height: 5px;
    border-radius: 50%;
    background: var(--color-verde);
    margin-top: 7px;
  }

  /* Alerta / Advertencia */
  .dtabs-alerta {
    display: flex;
    align-items: flex-start;
    gap: var(--space-2);
    background: #fffbf0;
    border: 1px solid #f0d280;
    border-left: 3px solid #d4a017;
    border-radius: var(--radius-md);
    padding: var(--space-2) var(--space-3);
    margin-top: var(--space-1);
  }
  .dtabs-alerta__icon {
    display: flex;
    flex-shrink: 0;
    color: #c47f0a;
    padding-top: 1px;
  }
  .dtabs-alerta__titulo {
    display: block;
    font-size: 10px;
    font-weight: var(--font-weight-medium);
    letter-spacing: var(--letter-spacing-wide);
    text-transform: uppercase;
    color: #b07000;
    margin-bottom: 3px;
  }
  .dtabs-alerta__texto {
    font-size: 12.5px;
    font-weight: var(--font-weight-light);
    line-height: var(--line-height-normal);
    color: var(--text-secondary);
  }

  /* Refinamientos Desktop */
  @media (min-width: 900px) {
    .dtabs-tab {
      font-size: 13px;
      padding: 9px 18px;
    }
    .dtabs-text,
    .dtabs-list__item {
      font-size: var(--font-size-body);
      line-height: var(--line-height-loose);
    }
    .dtabs-list__dot { margin-top: 8px; }
  }
`;

const IconCart = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none"
    stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="9" cy="21" r="1"/>
    <circle cx="20" cy="21" r="1"/>
    <path d="M1 1h4l2.68 13.39a2 2 0 0 0 2 1.61h9.72a2 2 0 0 0 2-1.61L23 6H6"/>
  </svg>
);

const IconWhatsApp = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
  </svg>
);

const ProductDetail = ({ addToCart }) => {
  const { id }     = useParams();
  const location   = useLocation();
  const navigate   = useNavigate();

  // Consumimos el contexto de afiliado
  const { afiliado, vistaAfiliado } = useAfiliado();
  const esModoAfiliado = Boolean(afiliado && vistaAfiliado);

  const [producto, setProducto] = useState(location.state?.producto || null);
  const [loading, setLoading]   = useState(!location.state?.producto);
  const [errorMsg, setErrorMsg] = useState(null);
  const [imgError, setImgError] = useState(false);
  const [zoom, setZoom]         = useState(false);
  const [mousePos, setMousePos] = useState({ x: 50, y: 50 });

  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'Escape' && zoom) {
        setZoom(false);
        setMousePos({ x: 50, y: 50 });
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [zoom]);

  const handleMouseMove = (e) => {
    if (!zoom) return;
    const rect = e.currentTarget.getBoundingClientRect();
    const x = Math.max(0, Math.min(100, ((e.clientX - rect.left) / rect.width) * 100));
    const y = Math.max(0, Math.min(100, ((e.clientY - rect.top) / rect.height) * 100));
    setMousePos({ x, y });
  };

  const handleMouseLeave = () => {
    if (zoom) {
      setMousePos({ x: 50, y: 50 });
    }
  };

  const toggleZoom = () => {
    setZoom((prev) => {
      if (prev) {
        setMousePos({ x: 50, y: 50 });
      }
      return !prev;
    });
  };

  useEffect(() => {
    setErrorMsg(null);
    setImgError(false);
    setZoom(false);

    const productoDelState = location.state?.producto;

    if (productoDelState && String(productoDelState.id) === String(id)) {
      setProducto(productoDelState);
      setLoading(false);
      return;
    }

    const fetchProducto = async () => {
      try {
        setLoading(true);
        const ref  = doc(db, 'catalogo', id);
        const snap = await getDoc(ref);
        if (snap.exists()) {
          setProducto({ id: snap.id, ...snap.data() });
        } else {
          setErrorMsg(`No existe el producto con ID: ${id}`);
          setProducto(null);
        }
      } catch (error) {
        setErrorMsg(`${error.code || 'error'}: ${error.message}`);
        setProducto(null);
      } finally {
        setLoading(false);
      }
    };
    fetchProducto();
  }, [id, location.state]);

  // ── Cálculo de precios dinámico ──
  const precioBase = Number(producto?.precio || producto?.precioNormal || 0);
  const precioOferta = Number(producto?.precioOferta || 0);

  // Precio específico de afiliado si viene configurado en la BD
  const precioAfilExplicito =
    producto?.precioAfiliado ??
    producto?.precio_afiliado ??
    producto?.comercial?.precio_afiliado ??
    producto?.comercial?.precioAfiliado;

  let precioFinal = precioBase;
  let precioTachado = null;
  let etiquetaDescuento = null;

  if (esModoAfiliado) {
    if (precioAfilExplicito != null && Number(precioAfilExplicito) > 0) {
      precioFinal = Number(precioAfilExplicito);
    } else if (precioBase > 0) {
      precioFinal = precioBase * 0.75; // 25% descuento de afiliado
    }
    if (precioBase > precioFinal) {
      precioTachado = precioBase;
      etiquetaDescuento = '25% AFILIADO';
    }
  } else {
    const tieneOferta = precioOferta > 0 && precioOferta < precioBase;
    if (tieneOferta) {
      precioFinal = precioOferta;
      precioTachado = precioBase;
      etiquetaDescuento = 'OFERTA';
    }
  }

  const urlWhatsApp = producto
    ? `https://wa.me/${WHATSAPP_NUMERO}?text=${encodeURIComponent(
        esModoAfiliado
          ? `Hola, soy ${afiliado.nombre} (afiliado). Tengo una consulta sobre el producto "${producto.nombre}". ¿Me pueden ayudar?`
          : `Hola, tengo una pregunta sobre el producto "${producto.nombre}". ¿Me pueden ayudar?`
      )}`
    : '#';

  // ── Botón volver ──
  const BotonVolver = () => (
    <button
      onClick={() => navigate('/')}
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 'var(--space-1)',
        background: 'none',
        border: 'none',
        padding: 'var(--space-1) 0',
        marginBottom: 'var(--space-2)',
        color: 'var(--text-muted)',
        fontFamily: 'var(--font-body)',
        fontSize: 'var(--font-size-small)',
        fontWeight: 'var(--font-weight-medium)',
        cursor: 'pointer',
        transition: 'color var(--transition-fast)',
      }}
      onMouseEnter={(e) => e.currentTarget.style.color = 'var(--color-verde)'}
      onMouseLeave={(e) => e.currentTarget.style.color = 'var(--text-muted)'}
    >
      <svg width="15" height="15" viewBox="0 0 24 24" fill="none"
        stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
        <path d="M15 18l-6-6 6-6"/>
      </svg>
      Volver al catálogo
    </button>
  );

  if (loading) {
    return (
      <div style={{
        padding: 'var(--space-8) var(--space-5)',
        textAlign: 'center',
        fontFamily: 'var(--font-body)',
        color: 'var(--text-muted)',
      }}>
        Cargando producto…
      </div>
    );
  }

  if (errorMsg || !producto) {
    return (
      <div style={{
        maxWidth: 'var(--max-width)',
        margin: '0 auto',
        padding: 'var(--space-5) var(--pad-section-x)',
        fontFamily: 'var(--font-body)',
      }}>
        <BotonVolver />
        <h2 style={{
          fontFamily: 'var(--font-display)',
          fontWeight: 'var(--font-weight-black)',
          color: 'var(--text-primary)',
          textAlign: 'center',
          marginTop: 'var(--space-6)',
        }}>
          No se pudo cargar la información
        </h2>
      </div>
    );
  }

  const imagenRaw = producto.imagenUrl || producto.imagen;
  const imagenSrc = getOptimizedUrl(imagenRaw, 1200);

  return (
    <div className="pd-container">

      <BotonVolver />

      {/* Nombre visible en móviles (< 900px) */}
      <h1 className="pd-title-mobile">
        {producto.nombre}
      </h1>

      {/* Layout de dos columnas responsive */}
      <div className="pd-grid">

        {/* ── Columna imagen ── */}
        <div className="pd-image-col">
          <div
            className="pd-image-card"
            onMouseMove={handleMouseMove}
            onMouseLeave={handleMouseLeave}
          >

            {/* Badge sobre imagen para modo afiliado */}
            {esModoAfiliado && (
              <span className="pd-affiliate-badge">
                25% AFILIADO
              </span>
            )}

            {/* Botón lupa zoom */}
            {!imgError && imagenRaw && (
              <button
                type="button"
                onClick={toggleZoom}
                aria-label={zoom ? 'Alejar imagen' : 'Acercar imagen'}
                title={zoom ? 'Alejar (Esc o clic)' : 'Acercar imagen'}
                className="pd-zoom-btn"
              >
                <svg width="17" height="17" viewBox="0 0 24 24" fill="none"
                  stroke="var(--text-primary)" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                  <circle cx="11" cy="11" r="8"/>
                  <line x1="21" y1="21" x2="16.65" y2="16.65"/>
                  {zoom
                    ? <line x1="8" y1="11" x2="14" y2="11"/>
                    : <>
                        <line x1="11" y1="8" x2="11" y2="14"/>
                        <line x1="8" y1="11" x2="14" y2="11"/>
                      </>
                  }
                </svg>
              </button>
            )}

            {!imgError && imagenRaw ? (
              <img
                src={imagenSrc}
                alt={producto.nombre}
                onError={() => setImgError(true)}
                onClick={toggleZoom}
                className="pd-main-img"
                style={{
                  transformOrigin: zoom ? `${mousePos.x}% ${mousePos.y}%` : 'center center',
                  transform: zoom ? 'scale(2)' : 'scale(1)',
                  cursor: zoom ? 'zoom-out' : 'zoom-in',
                }}
              />
            ) : (
              <div className="pd-no-img">
                <svg width="36" height="36" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                  <rect x="3" y="3" width="18" height="18" rx="2" ry="2"/>
                  <circle cx="8.5" cy="8.5" r="1.5"/>
                  <polyline points="21 15 16 10 5 21"/>
                </svg>
                <span>Sin imagen disponible</span>
              </div>
            )}

            {/* Hint informativo de zoom en PC */}
            {!imgError && imagenRaw && (
              <div className={`pd-zoom-hint ${zoom ? 'is-active' : ''}`}>
                {zoom ? 'Mueve el cursor para explorar • Clic para alejar' : 'Haz clic para ampliar'}
              </div>
            )}
          </div>
        </div>

        {/* ── Columna información ── */}
        <div className="pd-info-col">
          {/* Nombre visible en Desktop (>= 900px) */}
          <h1 className="pd-title-desktop">
            {producto.nombre}
          </h1>

          {/* Precio dinámico */}
          <div style={{
            display: 'flex',
            alignItems: 'baseline',
            gap: 'var(--space-3)',
            flexWrap: 'wrap',
            marginBottom: 'var(--space-3)',
          }}>
            {precioTachado != null && (
              <span style={{
                fontSize: 'var(--font-size-body)',
                color: 'var(--text-disabled)',
                textDecoration: 'line-through',
                fontWeight: 'var(--font-weight-regular)',
              }}>
                ${Number(precioTachado).toFixed(2)}
              </span>
            )}

            <span style={{
              fontFamily: 'var(--font-display)',
              fontSize: 'var(--font-size-h1)',
              fontWeight: 'var(--font-weight-black)',
              color: esModoAfiliado ? 'var(--color-verde)' : 'var(--text-primary)',
              letterSpacing: 'var(--letter-spacing-tight)',
              lineHeight: 1,
            }}>
              ${Number(precioFinal).toFixed(2)}
            </span>

            {etiquetaDescuento && (
              <span style={{
                fontSize: 'var(--font-size-label)',
                fontWeight: 'var(--font-weight-medium)',
                color: 'var(--color-blanco)',
                backgroundColor: 'var(--color-verde)',
                padding: '2px 10px',
                borderRadius: 'var(--radius-pill)',
                letterSpacing: 'var(--letter-spacing-wide)',
                textTransform: 'uppercase',
              }}>
                {etiquetaDescuento}
              </span>
            )}
          </div>

          {/* Disponibilidad y envío */}
          <div style={{
            display: 'flex',
            flexDirection: 'column',
            gap: 'var(--space-1)',
            marginBottom: 'var(--space-3)',
          }}>
            <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: 'var(--space-2)',
              fontSize: 'var(--font-size-small)',
            }}>
              <span style={{
                fontWeight: 'var(--font-weight-medium)',
                color: 'var(--text-primary)',
              }}>
                Disponibilidad:
              </span>
              <span style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: '4px',
                fontWeight: 'var(--font-weight-medium)',
                color: 'var(--color-verde)',
              }}>
                <svg width="13" height="13" viewBox="0 0 24 24" fill="none"
                  stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M5 13l4 4L19 7"/>
                </svg>
                {producto.disponible === false ? 'Agotado' : 'En existencia'}
              </span>
            </div>

            <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: 'var(--space-2)',
              fontSize: 'var(--font-size-small)',
              color: 'var(--text-secondary)',
            }}>
              <svg width="15" height="15" viewBox="0 0 24 24" fill="none"
                stroke="var(--color-verde)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <rect x="1" y="6" width="14" height="11" rx="1"/>
                <path d="M15 10h4l3 3v4h-7"/>
                <circle cx="6" cy="19" r="2"/>
                <circle cx="18" cy="19" r="2"/>
              </svg>
              Envío a domicilio disponible
            </div>
          </div>

          {/* Separador */}
          <div style={{
            height: '1px',
            backgroundColor: 'var(--border-default)',
            marginBottom: 'var(--space-3)',
          }} />


          {/* Descripción estructurada en pestañas (si el texto tiene secciones detectables)
              o como texto plano si no se detecta estructura */}
          {(() => {
            const desc = producto.descripcion;
            if (!desc) return null;
            const secciones = parseDescripcion(desc);
            if (secciones) {
              return <TabsDescripcion secciones={secciones} />;
            }
            // Fallback: texto plano con el renderizador HTML anterior
            return (
              <div>
                <p className="dtabs-single-title">Descripción</p>
                <div
                  className="product-description"
                  style={{
                    fontFamily: 'var(--font-body)',
                    fontSize: 'var(--font-size-small)',
                    fontWeight: 'var(--font-weight-light)',
                    lineHeight: 'var(--line-height-loose)',
                    color: 'var(--text-secondary)',
                    marginTop: 'var(--space-2)',
                  }}
                  dangerouslySetInnerHTML={{ __html: formatearDescripcion(desc) }}
                />
              </div>
            );
          })()}


          {/* CTA principal */}
          <button
            onClick={() => addToCart(producto)}
            style={{
              width: '100%',
              padding: '13px var(--space-4)',
              backgroundColor: 'var(--color-verde)',
              color: 'var(--color-blanco)',
              border: 'none',
              borderRadius: 'var(--radius-md)',
              fontFamily: 'var(--font-body)',
              fontSize: 'var(--font-size-body)',
              fontWeight: 'var(--font-weight-medium)',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 'var(--space-2)',
              transition: 'background-color var(--transition-fast), transform var(--transition-fast)',
              marginTop: 'var(--space-4)',
              marginBottom: 'var(--space-2)',
            }}
            onMouseEnter={(e) => e.currentTarget.style.backgroundColor = 'var(--color-verde-hover)'}
            onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'var(--color-verde)'}
            onMouseDown={(e)  => e.currentTarget.style.transform = 'scale(0.99)'}
            onMouseUp={(e)    => e.currentTarget.style.transform = 'scale(1)'}
          >
            <IconCart />
            Agregar al carrito
          </button>

          {/* CTA secundaria */}
          <a
            href={urlWhatsApp}
            target="_blank"
            rel="noopener noreferrer"
            style={{
              width: '100%',
              padding: '12px var(--space-4)',
              backgroundColor: 'transparent',
              color: 'var(--text-primary)',
              border: '1px solid var(--border-default)',
              borderRadius: 'var(--radius-md)',
              fontFamily: 'var(--font-body)',
              fontSize: 'var(--font-size-small)',
              fontWeight: 'var(--font-weight-medium)',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 'var(--space-2)',
              textDecoration: 'none',
              boxSizing: 'border-box',
              transition: 'border-color var(--transition-fast), background-color var(--transition-fast)',
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.borderColor = 'var(--color-verde)';
              e.currentTarget.style.backgroundColor = 'var(--color-verde-tint)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.borderColor = 'var(--border-default)';
              e.currentTarget.style.backgroundColor = 'transparent';
            }}
          >
            <span style={{ color: 'var(--color-verde)', display: 'flex' }}>
              <IconWhatsApp />
            </span>
            ¿Necesitas ayuda con este producto? Escríbenos
          </a>

        </div>
      </div>

      {/* Productos relacionados */}
      <ProductosRelacionados producto={producto} />

      <style>{`
        .pd-container {
          max-width: 1180px;
          margin: 0 auto;
          padding: var(--space-2) var(--pad-section-x) var(--space-6);
          font-family: var(--font-body);
        }

        .pd-title-mobile {
          display: block;
          font-family: var(--font-display);
          font-size: var(--font-size-h2);
          font-weight: var(--font-weight-black);
          color: var(--text-primary);
          letter-spacing: var(--letter-spacing-tight);
          line-height: var(--line-height-tight);
          margin: 0 0 var(--space-3);
        }

        .pd-title-desktop {
          display: none;
          font-family: var(--font-display);
          font-size: clamp(24px, 2.5vw, 34px);
          font-weight: var(--font-weight-black);
          color: var(--text-primary);
          letter-spacing: var(--letter-spacing-tight);
          line-height: var(--line-height-tight);
          margin: 0 0 var(--space-3);
        }

        /* Layout Grid responsive */
        .pd-grid {
          display: grid;
          grid-template-columns: 1fr;
          gap: var(--space-4);
          align-items: start;
        }

        /* Columna de imagen */
        .pd-image-col {
          width: 100%;
          max-width: 480px;
          margin: 0 auto;
        }

        /* Card contenedor de imagen */
        .pd-image-card {
          width: 100%;
          aspect-ratio: 1 / 1;
          position: relative;
          background-color: var(--color-blanco);
          border: 1px solid var(--border-default);
          border-radius: var(--radius-lg, 12px);
          box-shadow: var(--shadow-sm);
          display: flex;
          align-items: center;
          justify-content: center;
          overflow: hidden;
          transition: border-color var(--transition-fast), box-shadow var(--transition-fast);
        }

        /* Imagen del producto */
        .pd-main-img {
          width: 100%;
          height: 100%;
          max-height: min(48vh, 420px);
          object-fit: contain;
          padding: var(--space-4);
          display: block;
          user-select: none;
          transition: transform 220ms cubic-bezier(0.2, 0, 0.2, 1);
        }

        /* Botón de lupa zoom */
        .pd-zoom-btn {
          position: absolute;
          top: var(--space-3);
          right: var(--space-3);
          z-index: 3;
          width: 36px;
          height: 36px;
          border-radius: 50%;
          border: 1px solid var(--border-default);
          background-color: var(--color-blanco);
          box-shadow: var(--shadow-sm);
          display: flex;
          align-items: center;
          justify-content: center;
          cursor: pointer;
          -webkit-tap-highlight-color: transparent;
          transition: background-color var(--transition-fast), transform var(--transition-fast), border-color var(--transition-fast);
        }
        .pd-zoom-btn:hover {
          background-color: var(--color-gris-fondo);
          border-color: var(--color-gris-mid);
          transform: scale(1.05);
        }

        /* Badge de afiliado sobre la imagen */
        .pd-affiliate-badge {
          position: absolute;
          top: var(--space-3);
          left: var(--space-3);
          background-color: var(--color-verde);
          color: var(--color-blanco);
          font-family: var(--font-body);
          font-size: 11px;
          font-weight: 700;
          letter-spacing: 0.05em;
          padding: 4px 10px;
          border-radius: var(--radius-sm, 4px);
          z-index: 2;
          box-shadow: var(--shadow-sm);
        }

        /* Placeholder cuando no hay imagen */
        .pd-no-img {
          display: flex;
          flex-direction: column;
          align-items: center;
          gap: var(--space-2);
          color: var(--text-disabled);
          font-size: var(--font-size-small);
          padding: var(--space-6);
        }

        /* Hint informativo sobre zoom */
        .pd-zoom-hint {
          display: none;
        }

        /* Columna de información */
        .pd-info-col {
          display: flex;
          flex-direction: column;
          min-width: 0;
        }

        /* ── Estilos y adaptaciones para PC / Desktop (>= 900px) ── */
        @media (min-width: 900px) {
          .pd-container {
            padding-top: var(--space-3);
            padding-bottom: var(--space-8);
          }

          .pd-title-mobile {
            display: none;
          }

          .pd-title-desktop {
            display: block;
          }

          .pd-grid {
            grid-template-columns: minmax(0, 1.08fr) minmax(0, 1fr);
            gap: clamp(32px, 4vw, 56px);
            align-items: start;
          }

          .pd-image-col {
            max-width: none;
            margin: 0;
            position: sticky;
            top: calc(var(--header-total-height-desktop, 100px) + 24px);
            z-index: 5;
          }

          .pd-image-card {
            min-height: 480px;
            max-height: 560px;
          }

          .pd-image-card:hover {
            border-color: var(--color-gris-mid);
            box-shadow: var(--shadow-md);
          }

          .pd-main-img {
            max-height: 500px;
            padding: var(--space-5);
          }

          .pd-zoom-hint {
            display: flex;
            position: absolute;
            bottom: var(--space-3);
            left: 50%;
            transform: translateX(-50%);
            background: rgba(255, 255, 255, 0.94);
            backdrop-filter: blur(6px);
            padding: 5px 14px;
            border-radius: var(--radius-pill);
            border: 1px solid var(--border-default);
            font-size: 11px;
            font-weight: 500;
            color: var(--text-muted);
            pointer-events: none;
            opacity: 0;
            transition: opacity var(--transition-fast), color var(--transition-fast), border-color var(--transition-fast);
            white-space: nowrap;
            z-index: 2;
            box-shadow: var(--shadow-sm);
          }

          .pd-image-card:hover .pd-zoom-hint {
            opacity: 1;
          }

          .pd-zoom-hint.is-active {
            color: var(--color-verde);
            border-color: var(--color-verde-border);
          }
        }

        .product-description p { margin: 0 0 12px; }
        .product-description br { display: block; content: ""; margin-top: 12px; }
        .product-description strong {
          color: var(--text-primary);
          font-weight: var(--font-weight-medium);
        }
      `}{DTABS_CSS}</style>
    </div>
  );
};

export default ProductDetail;